import { deriveSuitabilityStatus, type LockedHoldingCap } from "@repo/shared";
import type {
  ExistingSnapshotPosition,
  GoalPortfolioSwitchPair,
  LocalizedString,
  MinBuyViolation,
} from "@repo/shared/types";
import {
  ArrowDownUp,
  ArrowRightLeft,
  Info,
  Lock,
  Pencil,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { OrderTypeBadge } from "@/components/orders/order-type-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import {
  frozenSuitabilityResult,
  getSuitabilityIssueSummary,
  type ProductSuitabilityResult,
  type ProductWithSuitabilityMeta,
  type SuitabilityContext,
} from "@/lib/services/suitability-service";
import type { CustomProduct, SuitabilityStatus } from "./custom-portfolio-builder-types";
import { T } from "./portfolio-product-table.i18n";
import { useMinBuyFollowupLine } from "./use-min-buy-followup-line";

type PortfolioProductTableProps = {
  userLanguage: string;
  customProducts: CustomProduct[];
  productsWithSuitability: ProductWithSuitabilityMeta[];
  suitabilityResults: Map<string, ProductSuitabilityResult>;
  suitabilityContext: SuitabilityContext | null;
  clientBypassesSuitability: boolean;
  showIsin: boolean;
  showProductType: boolean;
  isCompleted: boolean;
  /** Server-computed min-buy breaches (QP-193) — empty when the collection doesn't enforce them. */
  minBuyViolations?: MinBuyViolation[];
  onEditProduct: (product: CustomProduct) => void;
  onDeleteProduct: (productId: string) => void;
  /** Followup mode — show comparison columns */
  isFollowup?: boolean;
  /** Map of productId → existing amount from snapshot */
  existingAmountMap?: Map<string, number>;
  /** Map of productId → existing units from snapshot (units-type products) */
  existingUnitsMap?: Map<string, number>;
  /** QL-502 — productId → lock state for rows whose product is no longer purchasable (every flow type). */
  lockedByProductId?: Map<string, LockedHoldingCap>;
  /** Followup: snapshot holdings without a product reference (QL-203) —
   * rendered read-only on the existing side, never buyable or editable. */
  unmatchableHoldings?: ExistingSnapshotPosition[];
  /** QL-304 — opens the sell-amount dialog for an unmatchable holding.
   * Absent on the public flow and on completed instances — the row then shows
   * a read-only kept / will-be-sold marker instead. */
  onEditHoldingSell?: (holding: ExistingSnapshotPosition) => void;
  /** Unmatchable holding whose sell mutation is in flight. */
  busyHoldingId?: string | null;
  /** Followup: productId → wrapper syncKey of the pending account this NEW
   * mismatched instrument will open at completion (wrapper-aware followups). */
  newWrapperAccountByProductId?: Map<string, string>;
  /** QL-283 — switch pairs keyed by source position id. */
  switchPairsBySource?: Map<string, GoalPortfolioSwitchPair[]>;
  /** QL-283 — snapshot position id per matched productId (pair lookup). */
  positionIdByProductId?: Map<string, string>;
  /** QL-283 — opens the switch-pair dialog for a holding. Absent on
   * completed instances and agreement followups. */
  onOpenSwitch?: (source: {
    positionId: string;
    productId: string | null;
    /** True when the holding already has a delta/sell instruction (regime). */
    blocked: boolean;
  }) => void;
  /** QL-283 — completed instances only: opens the switch dialog read-only so
   * the advised pairs stay inspectable after completion. */
  onViewSwitch?: (positionId: string) => void;
};

export function PortfolioProductTable({
  userLanguage,
  customProducts,
  productsWithSuitability,
  suitabilityResults,
  suitabilityContext,
  clientBypassesSuitability,
  showIsin,
  showProductType,
  isCompleted,
  minBuyViolations = [],
  onEditProduct,
  onDeleteProduct,
  isFollowup = false,
  existingAmountMap = new Map(),
  existingUnitsMap = new Map(),
  lockedByProductId = new Map(),
  unmatchableHoldings = [],
  newWrapperAccountByProductId = new Map(),
  onEditHoldingSell,
  busyHoldingId = null,
  switchPairsBySource = new Map(),
  positionIdByProductId = new Map(),
  onOpenSwitch,
  onViewSwitch,
}: PortfolioProductTableProps) {
  const t = useT(T);
  const minBuyFollowupLine = useMinBuyFollowupLine();
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const totalAmount = customProducts.reduce((sum, p) => sum + p.amount, 0);

  // QL-304/QL-283 — one Direction badge per row (account follow-ups only):
  // what the row does to the client's money at completion — a matched delta,
  // an unmatchable sell decision, or switch pairs underneath. The badge
  // itself (variant + label) is the shared OrderTypeBadge; only the
  // per-direction tooltip is this table's own.
  const directionBadge = (direction: "buy" | "sell" | "keep" | "switch") => (
    <OrderTypeBadge
      orderType={direction}
      language={userLanguage}
      title={
        direction === "buy"
          ? t.directionBuyTooltip
          : direction === "sell"
            ? t.directionSellTooltip
            : direction === "switch"
              ? t.directionSwitchTooltip
              : t.directionKeepTooltip
      }
    />
  );

  // Keyed by product for the row icon (first entry wins — a product can repeat
  // on an account follow-up, one entry per switch pair) and by pair for the
  // pair lines. Switch entries never flag the TARGET's row: the purchase they
  // judge is the pair, which is where the alert sits.
  const minBuyViolationByProductId = new Map<string, MinBuyViolation>();
  const minBuyViolationByPairId = new Map<string, MinBuyViolation>();
  for (const v of minBuyViolations) {
    if (v.followup?.switchPairId) minBuyViolationByPairId.set(v.followup.switchPairId, v);
    else if (!minBuyViolationByProductId.has(v.productId)) {
      minBuyViolationByProductId.set(v.productId, v);
    }
  }
  const minBuyTooltipText = (v: MinBuyViolation): string =>
    v.followup ? minBuyFollowupLine(v) : t.minBuyTooltip(formatNumber(v.minBuyAmount));

  // QL-283 — indented pair lines rendered under a source holding's name:
  // "↷ 30 % → Fund B" / "↷ 30 units → Fund B".
  // QL-398 — a pair the gate flags below its target's minimum carries an
  // alert (backstop: data that predates a raised minimum).
  const switchPairLines = (pairs: GoalPortfolioSwitchPair[]) => (
    <div className="mt-0.5 space-y-0.5">
      {pairs.map((pair) => (
        <div key={pair.id} className="pl-4 text-xs text-muted-foreground">
          {minBuyViolationByPairId.has(pair.id) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TriangleAlert
                  className="mr-1 inline h-3.5 w-3.5 text-destructive cursor-help"
                  aria-label={t.belowMinBuy}
                />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                {minBuyTooltipText(minBuyViolationByPairId.get(pair.id) as MinBuyViolation)}
              </TooltipContent>
            </Tooltip>
          )}
          ↷{" "}
          {pair.switchUnits != null
            ? `${pair.switchUnits} ${t.units.toLowerCase()}`
            : `${pair.switchPercent}%`}{" "}
          → {localize(pair.targetProductName as LocalizedString) || pair.targetIsin || "—"}
        </div>
      ))}
    </div>
  );

  // QL-283 — the Switch row action, shared by matched and unmatchable rows.
  const switchActionButton = (source: {
    positionId: string;
    productId: string | null;
    blocked: boolean;
  }) =>
    onOpenSwitch && (
      <Button
        size="icon"
        tooltip={source.blocked ? t.switchBlockedDeltaTooltip : t.switchAction}
        variant="ghost"
        disabled={source.blocked}
        onClick={() => onOpenSwitch(source)}
      >
        <ArrowRightLeft className="h-4 w-4" />
      </Button>
    );

  // QL-283 — completed instances: the ⓘ beside the Switch badge keeps the
  // advised pairs inspectable (read-only dialog) after the actions column
  // is gone.
  const switchViewButton = (positionId: string) =>
    onViewSwitch && (
      <Button
        size="icon"
        tooltip={t.switchViewAction}
        variant="ghost"
        className="h-6 w-6"
        onClick={() => onViewSwitch(positionId)}
      >
        <Info className="h-4 w-4" />
      </Button>
    );

  // Determine suitability status for a product
  // QL-707 — a locked row carries the verdict frozen at submit; it never reads
  // the live collection, so a product deleted or deactivated since keeps its label.
  const frozenVerdict = (cp: CustomProduct) => cp.suitability; // served on locked reads only

  const getSuitabilityStatus = (cp: CustomProduct): SuitabilityStatus => {
    const frozen = frozenVerdict(cp);
    if (frozen !== undefined) return frozen?.status ?? "unknown";

    if (clientBypassesSuitability) return "suitable";

    const product = productsWithSuitability.find((p) => p.id === cp.productId);
    if (!product || !suitabilityContext || !suitabilityResults.has(cp.productId)) return "unknown";
    // The shared rule decides the label (the same function the frozen verdict uses).
    return deriveSuitabilityStatus(product, suitabilityContext).status;
  };

  return (
    <Table variant="data">
      <TableHeader>
        <TableRow>
          <TableHead>{t.tableHeaderName}</TableHead>
          {showIsin && <TableHead>{t.tableHeaderIsin}</TableHead>}
          {showProductType && <TableHead>{t.tableHeaderType}</TableHead>}
          <TableHead>{t.tableHeaderSuitable}</TableHead>
          {isFollowup && <TableHead className="text-right">{t.tableHeaderExisting}</TableHead>}
          <TableHead className="text-right">{t.tableHeaderAmount}</TableHead>
          {isFollowup && <TableHead className="text-right">{t.tableHeaderChange}</TableHead>}
          <TableHead className="text-right">{t.tableHeaderWeight}</TableHead>
          {isFollowup && <TableHead className="text-right">{t.tableHeaderDirection}</TableHead>}
          {!isCompleted && <TableHead className="text-right">{t.tableHeaderActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        <TooltipProvider>
          {customProducts.map((cp) => {
            const suitabilityStatus = getSuitabilityStatus(cp);
            const frozen = frozenVerdict(cp);
            const suitability =
              frozen !== undefined
                ? frozen
                  ? frozenSuitabilityResult(cp.productId, frozen, userLanguage)
                  : undefined
                : suitabilityResults.get(cp.productId);
            // QL-283 — switch state of a matched from-account row.
            const sourcePositionId = positionIdByProductId.get(cp.productId);
            const rowPairs = sourcePositionId
              ? (switchPairsBySource.get(sourcePositionId) ?? [])
              : [];
            const hasPairs = rowPairs.length > 0;
            // QL-502 — lock state mirrors the server's purchasability cap on
            // every flow type (see lockedHoldingCap); remove-only = locked
            // with no frozen baseline (plain/agreement followups, and a new
            // non-held row on an account followup).
            const lock = lockedByProductId.get(cp.productId);
            const removeOnly = !!lock?.locked && !lock.frozen;
            const rowExistingAmount = existingAmountMap.get(cp.productId);
            const rowExistingUnits = existingUnitsMap.get(cp.productId);
            const rowIsUnits = cp.product.orderAmountType === "units" && cp.units != null;
            // A delta in progress blocks entering the switch regime.
            const hasDelta =
              rowExistingAmount != null &&
              (rowIsUnits && rowExistingUnits != null
                ? cp.units! !== rowExistingUnits
                : cp.amount !== rowExistingAmount);

            return (
              <TableRow
                key={cp.id}
                className={suitabilityStatus === "unsuitable" ? "bg-destructive/5" : undefined}
              >
                <TableCell>
                  <span className="font-medium inline-flex items-center gap-1.5">
                    {localize(cp.product.nameI18n as LocalizedString)}
                    {lock?.locked && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={
                                lock.frozen ? t.notAvailableForPurchase : t.lockedRemoveOnly
                              }
                              className="inline-flex cursor-pointer"
                            >
                              <Lock className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            {lock.frozen ? t.notAvailableForPurchase : t.lockedRemoveOnly}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {isFollowup && newWrapperAccountByProductId.has(cp.productId) && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="cursor-default">
                              {t.newWrapperAccountBadge(
                                (
                                  newWrapperAccountByProductId.get(cp.productId) as string
                                ).toUpperCase(),
                              )}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            {t.newWrapperAccountTooltip(
                              (
                                newWrapperAccountByProductId.get(cp.productId) as string
                              ).toUpperCase(),
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                  {hasPairs && switchPairLines(rowPairs)}
                </TableCell>
                {showIsin && (
                  <TableCell className="text-muted-foreground">{cp.product.isin}</TableCell>
                )}
                {showProductType && (
                  <TableCell className="text-muted-foreground">
                    {cp.product.productType?.nameI18n
                      ? localize(cp.product.productType.nameI18n as LocalizedString)
                      : ""}
                  </TableCell>
                )}
                <TableCell>
                  {suitabilityStatus === "unknown" ? (
                    <Badge variant="secondary" className="text-xs">
                      {t.unknown}
                    </Badge>
                  ) : suitabilityStatus === "suitable" ? (
                    <Badge variant="success" className="text-xs">
                      {t.suitable}
                    </Badge>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="destructive" className="text-xs cursor-help">
                          {t.notSuitable}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs">
                        {suitability && getSuitabilityIssueSummary(suitability)}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                {isFollowup && (
                  <TableCell className="text-right">
                    {cp.product.orderAmountType === "units" &&
                    existingUnitsMap.has(cp.productId) ? (
                      <span>
                        {existingUnitsMap.get(cp.productId)!} {t.units.toLowerCase()}
                        {existingAmountMap.has(cp.productId) && (
                          <span className="text-muted-foreground">
                            {" "}
                            ({formatNumber(existingAmountMap.get(cp.productId)!)})
                          </span>
                        )}
                      </span>
                    ) : existingAmountMap.has(cp.productId) ? (
                      formatNumber(existingAmountMap.get(cp.productId)!)
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <span className="inline-flex items-center gap-1.5 justify-end">
                    {minBuyViolationByProductId.has(cp.productId) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <TriangleAlert
                            className="h-4 w-4 text-destructive cursor-help"
                            aria-label={t.belowMinBuy}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs">
                          {minBuyTooltipText(
                            minBuyViolationByProductId.get(cp.productId) as MinBuyViolation,
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {cp.product.orderAmountType === "units" && cp.units != null ? (
                      <span>
                        {cp.units} {t.units.toLowerCase()}
                        <span className="text-muted-foreground">
                          {" "}
                          ({formatNumber(cp.amount)} {t.estimated.toLowerCase()})
                        </span>
                      </span>
                    ) : (
                      formatNumber(cp.amount)
                    )}
                  </span>
                </TableCell>
                {isFollowup &&
                  (() => {
                    const isUnitsRow = cp.product.orderAmountType === "units" && cp.units != null;
                    const existingAmount = existingAmountMap.get(cp.productId);
                    if (existingAmount == null) {
                      return (
                        <TableCell className="text-right">
                          <span className="text-success text-sm font-medium">
                            +
                            {isUnitsRow
                              ? `${cp.units} ${t.units.toLowerCase()}`
                              : formatNumber(cp.amount)}{" "}
                            ({t.newProduct})
                          </span>
                        </TableCell>
                      );
                    }
                    // QL-283 — a switch source is locked at its frozen
                    // holding, so the regular delta is 0; the net switched
                    // share IS the row's change, verbatim from the stored
                    // pair instructions (units or integer percent). Info
                    // blue like the Switch badge — the value moves within
                    // the portfolio, it does not leave it (that's red).
                    if (hasPairs) {
                      const switchedUnits = rowPairs.reduce((s, p) => s + (p.switchUnits ?? 0), 0);
                      const switchedPercent = rowPairs.reduce(
                        (s, p) => s + (p.switchPercent ?? 0),
                        0,
                      );
                      return (
                        <TableCell className="text-right">
                          <span className="text-info text-sm font-medium">
                            −
                            {switchedUnits > 0
                              ? `${switchedUnits} ${t.units.toLowerCase()}`
                              : `${switchedPercent}%`}
                          </span>
                        </TableCell>
                      );
                    }
                    // Units products with a frozen unit count change in units;
                    // everything else changes in value.
                    const existingUnits = existingUnitsMap.get(cp.productId);
                    const delta =
                      isUnitsRow && existingUnits != null
                        ? cp.units! - existingUnits
                        : cp.amount - existingAmount;
                    const format = (n: number) =>
                      isUnitsRow && existingUnits != null
                        ? `${n} ${t.units.toLowerCase()}`
                        : formatNumber(n);
                    return (
                      <TableCell className="text-right">
                        {delta > 0 ? (
                          <span className="text-success text-sm font-medium">+{format(delta)}</span>
                        ) : delta < 0 ? (
                          <span className="text-destructive text-sm font-medium">
                            {format(delta)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    );
                  })()}
                <TableCell className="text-right">{cp.weight.toFixed(1)}%</TableCell>
                {isFollowup &&
                  (() => {
                    if (hasPairs) {
                      return (
                        <TableCell className="text-right">
                          <span className="inline-flex items-center gap-1">
                            {sourcePositionId && switchViewButton(sourcePositionId)}
                            {directionBadge("switch")}
                          </span>
                        </TableCell>
                      );
                    }
                    const existingAmount = existingAmountMap.get(cp.productId);
                    const existingUnits = existingUnitsMap.get(cp.productId);
                    const isUnitsRow = cp.product.orderAmountType === "units" && cp.units != null;
                    const delta =
                      existingAmount == null
                        ? 1
                        : isUnitsRow && existingUnits != null
                          ? cp.units! - existingUnits
                          : cp.amount - existingAmount;
                    return (
                      <TableCell className="text-right">
                        {directionBadge(delta > 0 ? "buy" : delta < 0 ? "sell" : "keep")}
                      </TableCell>
                    );
                  })()}
                {!isCompleted && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* QL-283 — one regime per holding: while pairs exist the
                          target is locked and sell actions are hidden; the
                          switch button is the row's way in and out. A HELD
                          instrument's dialog is the Sell|Keep|Buy instruction
                          editor — vertical arrows to pair with the switch's
                          horizontal ones; new instruments keep the pencil
                          (their dialog is a plain buy-amount edit). */}
                      <Button
                        size="icon"
                        tooltip={
                          hasPairs
                            ? t.switchTargetLockedTooltip
                            : removeOnly
                              ? t.lockedRemoveOnly
                              : isFollowup && existingAmountMap.has(cp.productId)
                                ? t.adjustHoldingTooltip
                                : t.editProductAmount
                        }
                        variant="ghost"
                        disabled={hasPairs || removeOnly}
                        onClick={() => onEditProduct(cp)}
                      >
                        {isFollowup && existingAmountMap.has(cp.productId) ? (
                          <ArrowDownUp className="h-4 w-4" />
                        ) : (
                          <Pencil className="h-4 w-4" />
                        )}
                      </Button>
                      {isFollowup && existingAmountMap.has(cp.productId) ? (
                        // A from-account instrument is never deletable (server
                        // rule) — its exits are the dialog's Sell (incl. Sell
                        // all) or a switch, so no delete icon.
                        sourcePositionId &&
                        switchActionButton({
                          positionId: sourcePositionId,
                          productId: cp.productId,
                          blocked: !hasPairs && hasDelta,
                        })
                      ) : (
                        <Button
                          size="icon"
                          tooltip={t.deleteProduct}
                          variant="ghost"
                          onClick={() => onDeleteProduct(cp.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {/* Unmatchable holdings (QL-203) — frozen from the source account but
              not matched to a collection product. Existing side only: no new
              amount, no change, no weight, no actions. */}
          {isFollowup &&
            unmatchableHoldings.map((h) => {
              // QL-283 — an unmatchable holding is under the keep/sell regime
              // OR the switch regime, never both.
              const holdingPairs = switchPairsBySource.get(h.id) ?? [];
              const holdingHasPairs = holdingPairs.length > 0;
              return (
                <TableRow key={h.id}>
                  <TableCell>
                    <span className="font-medium inline-flex items-center gap-1.5">
                      {h.name || "—"}
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={t.unmatchableHoldingTooltip}
                              className="inline-flex cursor-pointer"
                            >
                              <Lock className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            {t.unmatchableHoldingTooltip}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </span>
                    {holdingHasPairs && switchPairLines(holdingPairs)}
                  </TableCell>
                  {showIsin && (
                    <TableCell className="text-muted-foreground">{h.isin ?? "—"}</TableCell>
                  )}
                  {showProductType && <TableCell />}
                  <TableCell />
                  <TableCell className="text-right">{formatNumber(h.amount)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                  {/* Change column — the sell decision (QL-304). A sell shows the
                      chosen amount leaving; read-only surfaces (completed
                      instances) show a kept / will-be-sold badge with the
                      partial figure. Switched holdings show their net switched
                      share (units or percent, verbatim from the pair
                      instructions) in the Switch badge's info blue. */}
                  <TableCell className="text-right">
                    {holdingHasPairs ? (
                      (() => {
                        const switchedUnits = holdingPairs.reduce(
                          (s, p) => s + (p.switchUnits ?? 0),
                          0,
                        );
                        const switchedPercent = holdingPairs.reduce(
                          (s, p) => s + (p.switchPercent ?? 0),
                          0,
                        );
                        return (
                          <span className="text-info text-sm font-medium">
                            −
                            {switchedUnits > 0
                              ? `${switchedUnits} ${t.units.toLowerCase()}`
                              : `${switchedPercent}%`}
                          </span>
                        );
                      })()
                    ) : onEditHoldingSell ? (
                      h.sellAmount != null ? (
                        <span className="text-destructive text-sm font-medium">
                          −{formatNumber(h.sellAmount)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )
                    ) : h.sellAmount != null ? (
                      <Badge variant="destructive" className="text-xs">
                        {t.willBeSoldAmount(formatNumber(h.sellAmount), formatNumber(h.amount))}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {t.kept}
                      </Badge>
                    )}
                  </TableCell>
                  {/* Weight of the unsold remainder in the full advisable
                      portfolio — same denominator as the matched rows'
                      persisted weights, so the column sums to 100. */}
                  <TableCell className="text-right">
                    {h.weight != null ? (
                      `${h.weight.toFixed(1)}%`
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center gap-1">
                      {holdingHasPairs && switchViewButton(h.id)}
                      {directionBadge(
                        holdingHasPairs ? "switch" : h.sellAmount != null ? "sell" : "keep",
                      )}
                    </span>
                  </TableCell>
                  {!isCompleted && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Mirrors the matched rows' edit affordance: while
                            switch pairs hold the regime the button stays
                            VISIBLE but disabled with the lock explanation —
                            hiding it read as a missing feature. */}
                        {onEditHoldingSell && (
                          <Button
                            size="icon"
                            tooltip={
                              holdingHasPairs ? t.switchSellBlockedTooltip : t.editSellAmount
                            }
                            variant="ghost"
                            disabled={holdingHasPairs || busyHoldingId === h.id}
                            onClick={() => onEditHoldingSell(h)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {switchActionButton({
                          positionId: h.id,
                          productId: null,
                          blocked: !holdingHasPairs && h.sellAmount != null,
                        })}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
        </TooltipProvider>
        {/* Total Row */}
        <TableRow className="bg-muted/50 font-medium">
          <TableCell>{t.totalProducts(customProducts.length)}</TableCell>
          {showIsin && <TableCell />}
          {showProductType && <TableCell />}
          <TableCell />
          {isFollowup && <TableCell />}
          <TableCell className="text-right">{formatNumber(totalAmount)}</TableCell>
          {isFollowup && <TableCell />}
          <TableCell className="text-right">100%</TableCell>
          {isFollowup && <TableCell />}
          {!isCompleted && <TableCell />}
        </TableRow>
      </TableBody>
    </Table>
  );
}
